import React, { useState, useEffect, lazy, Suspense } from "react";
import { TopperMindset } from "../components/TopperMindset";
import { TopperSpotlight } from "../components/TopperSpotlight";
import { WorkshopBanner } from "../components/WorkshopBanner";
import { PillarCard } from "../components/PillarCard";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Sparkles, 
  ArrowRight, 
  Play, 
  ShieldCheck, 
  Timer, 
  Users, 
  BookOpen, 
  Award, 
  Library, 
  CheckCircle2, 
  Building2, 
  GraduationCap, 
  Calendar, 
  FileText, 
  PhoneCall, 
  TrendingUp, 
  ChevronRight, 
  Clock, 
  HelpCircle, 
  BadgeCheck, 
  Flame, 
  Check,
  Search,
  ChevronDown,
  Download,
  Lightbulb
} from "lucide-react";
const DemoBookingModal = lazy(() => import("../components/DemoBookingModal"));
import DirectorVideoCard from "../components/DirectorVideoCard";
import SectionDivider from "../components/SectionDivider";
import ScrollFade from "../components/ScrollFade";
import SectionProgress from "../components/SectionProgress";
const TestimonialStrip = lazy(() => import("../components/TestimonialStrip"));
import TrustStrip from "../components/TrustStrip";
import { ProgramFinder } from "../components/ProgramFinder";
import { examTabClass } from "../lib/examTheme";
import { optimg } from "../lib/optimg";
const LandingPopup = lazy(() => import("../components/LandingPopup"));

export default function HomePage() {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [toppers, setToppers] = useState([]);
  const [faculty, setFaculty] = useState([]);
  const [methodology, setMethodology] = useState([]);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [heroReel, setHeroReel] = useState(null); // { url, content_type, fallbacks, source }
  const [desktop, setDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => setDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Hero video + landing popup are deferred until the browser is idle so they never compete with the LCP.
  const [heroReady, setHeroReady] = useState(false);
  const [heroResolved, setHeroResolved] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  useEffect(() => {
    const w = window;
    const idle = (cb, t) => (w.requestIdleCallback ? w.requestIdleCallback(cb, { timeout: t }) : setTimeout(cb, t));
    const cancel = (id) => (w.cancelIdleCallback ? w.cancelIdleCallback(id) : clearTimeout(id));
    const id1 = idle(() => setHeroReady(true), 2000);
    const id2 = setTimeout(() => setShowPopup(true), 3000);
    return () => { cancel(id1); clearTimeout(id2); };
  }, []);

  // Interactive Course Finder Widget State
  const [selectedGoalTab, setSelectedGoalTab] = useState("all");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cRes, tRes, fRes, mRes, hRes] = await Promise.all([
          axios.get(`${API_URL}/api/courses`),
          axios.get(`${API_URL}/api/toppers`),
          axios.get(`${API_URL}/api/faculty`),
          axios.get(`${API_URL}/api/methodology`),
          axios.get(`${API_URL}/api/site/hero-video`).catch(() => ({ data: null })),
        ]);
        setCourses(cRes.data || []);
        setToppers(tRes.data || []);
        setFaculty(fRes.data || []);
        setMethodology(mRes.data || []);
        setHeroReel(hRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setHeroResolved(true); // reel settled (data or null) — safe to mount the video once, no refetch
      }
    };
    fetchData();
  }, [API_URL]);

  const filteredCourses = courses.filter((c) => {
    if (selectedGoalTab === "all") return true;
    return c.target_exam.toLowerCase().includes(selectedGoalTab.toLowerCase()) || 
           c.title.toLowerCase().includes(selectedGoalTab.toLowerCase());
  });

  return (
    <div className="space-y-16 pb-20 bg-[#FDFAF3] text-[#111827]">
      {showPopup && <Suspense fallback={null}><LandingPopup /></Suspense>}
      <SectionProgress />

      {/* 1. HERO SECTION */}
      <section data-testid="home-hero" data-section="hero" data-section-label="Home" className="!mt-0 relative overflow-hidden py-8 sm:py-12 lg:py-16 border-b border-gray-200">
        {/* Latest hero reel — desktop only. Phones/tablets get a plain cream hero (no video download). Managed from Admin → Hero Reel CMS. */}
        {desktop && <div className="absolute inset-0 -z-0 overflow-hidden bg-cover bg-center" data-testid="home-hero-video" style={{ backgroundImage: "url(/assets/real/brochure_page_10.webp)" }}>
          {heroReady && heroResolved && <video
            key={heroReel?.url || "user-hero"}
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="none"
            poster="/assets/real/brochure_page_10.webp"
            aria-hidden="true"
          >
            <source src="/user-hero.webm" type="video/webm" />
            <source src="/user-hero.mp4" type="video/mp4" />
            {heroReel?.url && !/user-hero/.test(heroReel.url) && (
              <source
                src={/^https?:\/\//.test(heroReel.url) ? heroReel.url : `${API_URL}${heroReel.url}`}
                type={heroReel.content_type || "video/mp4"}
              />
            )}
          </video>}
          {/* Legibility overlay: cream tint gradient (lighter on right so video peeks through) */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#FDFAF3]/95 via-[#FDFAF3]/70 to-[#FDFAF3]/35" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#FDFAF3]/70" />
        </div>}

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            
            {/* Left Content Column */}
            <div className="space-y-5 text-left">
              
              {/* Trust Pill — transparent orange w/ green live dot */}
              <p data-testid="home-hero-brand" className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-[#003399]">THE RANKERS ACADEMY <span className="block text-sm font-semibold text-slate-600 mt-1.5">VNIT alumni founded · <span data-testid="hero-admissions-blink" className="inline-block font-extrabold text-[#EA580C] animate-pulse">Admissions Open · 2027–29 Session</span></span></p>

              {/* Main Headline */}
              <h1 data-testid="home-hero-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#071E54] leading-[1.1]">
                Personal mentorship.<br />
                <span className="text-[#003399]">
                  Extraordinary ranks.
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed font-medium">
                JEE, NEET & MHT-CET preparation under VNIT alumni. Small batches, daily practice and personal attention — with your board syllabus included.
              </p>

              {/* Exam focus pills — gradient, glowing, pull attention */}
              <div className="flex flex-wrap gap-2 pt-1" data-testid="hero-exam-pills">
                {[
                  { label: "JEE", grad: "from-[#0A2A8C] via-[#003399] to-[#071E54]" },
                  { label: "NEET", grad: "from-[#0A2A8C] via-[#003399] to-[#071E54]" },
                  { label: "MHT-CET", grad: "from-[#0A2A8C] via-[#003399] to-[#071E54]" },
                  { label: "Foundation", grad: "from-[#0A2A8C] via-[#003399] to-[#071E54]" },
                ].map((p) => (
                  <Link key={p.label} to="/courses" data-testid={`hero-pill-${p.label.toLowerCase()}`} className={`hero-exam-pill bg-gradient-to-r ${p.grad}`}>
                    {p.label}
                  </Link>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="pt-1 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <button
                  data-testid="hero-book-free-demo-btn"
                  onClick={() => navigate("/demo")}
                  className="px-7 py-4 rounded-xl bg-[#003399] hover:bg-[#072468] text-white font-extrabold text-sm sm:text-base shadow-lg shadow-blue-900/20 hover:shadow-xl transition-all flex items-center justify-center space-x-3 group"
                >
                  <Calendar className="w-5 h-5 text-amber-300" />
                  <span>Free 1-Day Trial Pass</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>

                <Link
                  to="/rtse"
                  data-testid="hero-rtse-register-link"
                  className="px-6 py-3 rounded-xl bg-amber-400 hover:bg-amber-300 text-[#071E54] shadow-md flex items-center justify-center gap-2.5 transition-all border border-amber-500/30"
                >
                  <Award className="w-5 h-5 text-[#071E54] flex-shrink-0" />
                  <span className="flex flex-col leading-tight text-left">
                    <span className="font-black text-sm sm:text-base">RTSE 3.0 Scholarship</span>
                    <span className="font-semibold text-[10px] sm:text-xs text-[#071E54]/80">Rankers Talent Search cum Scholarship Examination</span>
                  </span>
                </Link>
              </div>

              {/* Prospectus download link */}
              <div className="pt-1">
                <a
                  href={`${API_URL}/api/site/prospectus/pdf`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="hero-download-prospectus"
                  className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-[#071E54] bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-lg px-3 py-2 shadow-sm transition-colors"
                >
                  <Download className="w-4 h-4 text-orange-600" />
                  <span>Download 2027–29 Prospectus <span className="text-orange-600">· PDF</span></span>
                </a>
              </div>

              {/* Key Quick Highlights Bar */}
              <div className="hidden sm:flex pt-4 flex-wrap items-center gap-4 text-xs font-bold text-gray-700">
                <div className="flex items-center space-x-1.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>Zero Mega-Batches (Max 40-45)</span>
                </div>
                <div className="flex items-center space-x-1.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>Integrated 11th/12th Boards Included</span>
                </div>
                <div className="flex items-center space-x-1.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>Laxmi Nagar, Nagpur (Mate Sq.)</span>
                </div>
              </div>

            </div>

          </div>
        </div>
      </section>
      <div className="!mt-0"><TrustStrip /></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Real Authentic Proof Stats Grid */}
          <div data-testid="home-proof-stats" className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="p-4 rounded-2xl bg-[#FDFAF3] border border-gray-200 shadow-sm flex items-center space-x-3.5">
              <div className="p-3 rounded-xl bg-amber-100 text-amber-800 shrink-0">
                <Award className="w-6 h-6" />
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-black text-[#071E54]"><span className="bg-amber-200/70 rounded px-1.5">100%</span></p>
                <p className="text-[11px] text-gray-600 font-bold">500/500 Marks in Class 10th (Srushti Rewatkar)</p>
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-[#FDFAF3] border border-gray-200 shadow-sm flex items-center space-x-3.5">
              <div className="p-3 rounded-xl bg-blue-100 text-[#003399] shrink-0">
                <TrendingUp className="w-6 h-6" />
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-black text-[#003399]">99.71 %ile</p>
                <p className="text-[11px] text-gray-600 font-bold">Top 1% State Engineering (Nidhi & Shwet)</p>
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-[#FDFAF3] border border-gray-200 shadow-sm flex items-center space-x-3.5">
              <div className="p-3 rounded-xl bg-emerald-100 text-emerald-800 shrink-0">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-black text-emerald-700">+194 ↑ Marks</p>
                <p className="text-[11px] text-gray-600 font-bold">Repeater 11-Month Jump (356 → 550)</p>
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-[#FDFAF3] border border-gray-200 shadow-sm flex items-center space-x-3.5">
              <div className="p-3 rounded-xl bg-indigo-100 text-indigo-900 shrink-0">
                <GraduationCap className="w-6 h-6" />
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-black text-[#071E54]">IIT Dhanbad</p>
                <p className="text-[11px] text-gray-600 font-bold">JEE Advanced 2026 Selection (Akshara)</p>
              </div>
            </div>
          </div>

      </div>

      <ProgramFinder />

      <SectionDivider label="Your Next Chapter" />

      {/* 1b. FREE ONLINE WORKSHOP — Free Trial Sessions Pass */}
      <section data-section="workshop" data-section-label="Free Workshop" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 mb-16">
        <WorkshopBanner />
      </section>

      {/* 2. CHOOSE YOUR GOAL / PROGRAM EXPLORER */}
      <ScrollFade>
      <section data-section="programs" data-section-label="Programs" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 border-b border-gray-200 pb-4">
          <div>
            <span className="text-xs font-black text-[#003399] uppercase tracking-widest">
              ACADEMIC OFFERINGS
            </span>
            <h2 className="text-2xl sm:text-4xl font-black text-[#071E54] mt-1">
              Classroom Programs & Batches
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-1 font-medium">
              Targeted courses designed by VNIT alumni. Integrated board coaching included.
            </p>
          </div>

          {/* Goal Switcher Tabs */}
          <div className="flex flex-wrap gap-1.5">
            {["all", "JEE", "NEET", "MHT-CET", "Repeaters", "Foundation"].map((tab) => (
              <button
                key={tab}
                data-testid={`goal-tab-${tab.toLowerCase()}`}
                onClick={() => setSelectedGoalTab(tab)}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-black tracking-tight transition-all duration-300 ${examTabClass(tab.toLowerCase(), selectedGoalTab === tab)}`}
              >
                {tab === "all" ? "All Programs" : tab}
              </button>
            ))}
          </div>
        </div>

        {/* Course Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCourses.map((course) => (
            <div
              key={course.code}
              data-testid={`course-card-${course.code}`}
              className="rounded-2xl bg-[#FDFAF3] border-2 border-[#003399]/40 hover:border-[#003399] p-6 flex flex-col justify-between transition-all duration-300 space-y-5 shadow-sm hover:shadow-md"
            >
              <div className="space-y-3.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-[#003399] px-2.5 py-0.5 rounded-md bg-blue-50 border border-blue-100">
                    {course.duration}
                  </span>
                  {course.badge && (
                    <span className="text-[10px] font-black px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300">
                      {course.badge}
                    </span>
                  )}
                </div>

                <h3 className="text-lg font-black text-[#071E54]">{course.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed font-medium">{course.description}</p>
                
                <div className="space-y-1.5 pt-2 border-t border-gray-100 text-xs text-gray-700">
                  {course.highlights.slice(0, 3).map((h, i) => (
                    <div key={i} className="flex items-start space-x-2 font-medium">
                      <Check className="w-3.5 h-3.5 text-[#003399] shrink-0 mt-0.5 font-bold" />
                      <span>{h}</span>
                    </div>
                  ))}
                </div>

                <div className="p-3 rounded-xl bg-gray-50 border border-gray-100 text-[11px] space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Commencing:</span>
                    <strong className="text-[#071E54]">{course.batch_starts}</strong>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Get Scholarship:</span>
                    <strong className="text-orange-600 bg-orange-100 rounded px-1.5 animate-pulse">Up to 90% via RTSE 3.0</strong>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  to="/rtse"
                  data-testid={`rtse-scholarship-for-${course.code}`}
                  className="w-full py-2.5 rounded-xl bg-[#003399] hover:bg-[#072468] text-white text-xs font-extrabold transition-colors flex items-center justify-center space-x-1.5 shadow-sm animate-pulse ring-2 ring-orange-400 shadow-[0_0_16px_rgba(234,88,12,0.55)]"
                >
                  <Lightbulb className="w-3.5 h-3.5 text-amber-300 drop-shadow-[0_0_6px_rgba(252,211,77,0.9)] animate-pulse" />
                  <span>Get Scholarship: Up to 90% via RTSE 3.0</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          ))}
        </div>

      </section>

      <SectionDivider label="The Proof" />

      {/* 3. RESULTS WALL & PROOF ARCHITECTURE */}
      </ScrollFade>
      <ScrollFade>
      <section data-section="results" data-section-label="Results" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        
        <div className="rounded-3xl bg-[#071E54] text-white p-8 sm:p-12 shadow-xl space-y-8">
          
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 border-b border-blue-900 pb-6">
            <div>
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-amber-400 text-[#071E54] text-xs font-black mb-2">
                <Award className="w-3.5 h-3.5 fill-current" />
                <span>VNIT STANDARD HALL OF FAME</span>
              </div>
              <h2 className="text-2xl sm:text-4xl font-black text-white">
                Proven Multi-Year Results & Score Jumps
              </h2>
              <p className="text-xs sm:text-sm text-slate-300 mt-1 font-medium">
                Verified results from our regular classroom batches in Laxmi Nagar, Nagpur.
              </p>
            </div>

            <Link
              to="/toppers"
              data-testid="topper-wall-explore-btn"
              className="px-5 py-2.5 rounded-xl bg-amber-400 hover:bg-amber-300 text-[#071E54] text-xs font-black flex items-center space-x-2 shadow-md self-start sm:self-auto"
            >
              <span>Explore All Rankers</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <TopperSpotlight topper={toppers.find((t) => t.spotlight)} />

          {/* Toppers Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {toppers.filter((t) => !t.spotlight).slice(0, 4).map((top, idx) => (
              <div
                key={idx}
                data-testid={`topper-card-${idx}`}
                className="p-5 rounded-2xl bg-[#0B2A70] border border-blue-800 flex flex-col justify-between space-y-3.5 relative overflow-hidden shadow-sm"
              >
                {top.is_repeater_jumper && (
                  <div className="absolute top-0 right-0 bg-emerald-600 text-white text-[9px] font-black px-2.5 py-0.5 rounded-bl-lg">
                    REPEATER JUMP
                  </div>
                )}
                
                <div className="flex items-start space-x-3">
                  <img
                    src={optimg(top.image_url || "/assets/real/brochure_page_3.webp", 128)}
                    alt={top.name}
                    width="56"
                    height="56"
                    loading="lazy"
                    decoding="async"
                    className="w-14 h-14 rounded-full object-cover object-center border-2 border-amber-400 shrink-0 bg-[#061840]"
                  />
                  <div>
                    <h4 className="text-sm font-black text-white">{top.name}</h4>
                    <p className="text-xs font-black text-amber-300">{top.rank}</p>
                    <p className="text-[11px] text-slate-200 font-bold">{top.score_or_percentile}</p>
                  </div>
                </div>

                <div className="p-2.5 rounded-xl bg-[#061840] text-[11px] border border-blue-900/60">
                  <span className="text-slate-400 font-medium">Admission / School:</span>
                  <p className="text-white font-bold">{top.college}</p>
                </div>

                <p className="text-[11px] text-slate-200 italic leading-relaxed">
                  "{top.quote}"
                </p>

                <div className="pt-2 border-t border-blue-800 flex items-center justify-between text-[10px] text-slate-300">
                  <span className="text-amber-300 font-bold">{top.exam}</span>
                  <span>{top.tag}</span>
                </div>
              </div>
            ))}
          </div>

        </div>

      </section>

      <SectionDivider label="Parent Voice Notes" />

      {/* 3b. PARENT WHATSAPP-STYLE TESTIMONIAL STRIP */}
      </ScrollFade>
      <ScrollFade>
      <Suspense fallback={null}><TestimonialStrip /></Suspense>

      <SectionDivider label="The Method" />

      {/* 4. THE RANKERS PEDAGOGICAL SYSTEM (6 Pillars) */}
      </ScrollFade>
      <ScrollFade>
      <section data-section="method" data-section-label="Method" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        
        <div className="text-center max-w-3xl mx-auto space-y-3">
          <span className="text-xs font-black px-3.5 py-1 rounded-full bg-blue-50 text-[#003399] border border-blue-200 uppercase tracking-wider">
            PEDAGOGICAL EXCELLENCE
          </span>
          <h2 className="text-2xl sm:text-4xl font-black text-[#071E54] tracking-tight">
            The 6-Pillar Rankers Formula
          </h2>
          <p className="text-xs sm:text-sm text-gray-500 font-medium">
            Clarity of concept from first principles, built into instinct through daily practice.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {methodology.map((pillar, idx) => <PillarCard key={pillar.number} pillar={pillar} idx={idx} />)}
        </div>

      </section>

      <SectionDivider label="Faculty Wall" />

      {/* 5. FOUNDERS + FACULTY WALL */}
      </ScrollFade>
      <ScrollFade>
      <section data-section="founders" data-section-label="Faculty" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl bg-[#FDFAF3] border-2 border-gray-200 p-8 sm:p-10 shadow-md space-y-8">

          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 border-b border-gray-100 pb-4">
            <div>
              <span className="text-xs font-black text-[#003399] uppercase tracking-wider">
                VNIT NAGPUR ALUMNI · TEACHING BENCH
              </span>
              <h2 className="text-2xl sm:text-3xl font-black text-[#071E54] mt-0.5">
                The Faces Who Teach You Every Single Day
              </h2>
            </div>
            <span className="text-xs px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 font-extrabold border border-emerald-200">
              ● {faculty.length} full-time faculty · 8-12 yrs each
            </span>
          </div>

          {/* Founder cards — prominent */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {faculty.filter((f) => f.is_founder).map((fac, idx) => (
              <div
                key={idx}
                data-testid={`founder-card-${idx}`}
                className="p-6 rounded-2xl bg-white border-2 border-[#003399]/20 hover:border-[#003399]/60 transition-all flex flex-col sm:flex-row gap-5 items-start shadow-sm hover:shadow-md"
              >
                <div className="relative shrink-0">
                  <img
                    src={optimg(fac.image_url, 320)}
                    alt={fac.name}
                    width="112"
                    height="112"
                    loading="lazy"
                    decoding="async"
                    className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover border-2 border-[#003399]"
                  />
                  <span className="absolute -bottom-2 -right-2 px-2 py-0.5 rounded-full bg-amber-400 text-[#0B132B] text-[9px] font-black uppercase tracking-widest border-2 border-white">
                    Founder
                  </span>
                </div>
                <div className="space-y-1.5 min-w-0">
                  <h3 className="text-lg font-black text-[#071E54]">{fac.name}</h3>
                  <p className="text-xs font-bold text-[#003399]">{fac.role}</p>
                  <p className="text-xs text-gray-500 font-medium">{fac.pedigree}</p>
                  <p className="text-xs text-gray-600 leading-relaxed pt-1 font-medium">{fac.bio}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Faculty wall — team members with monogram avatars */}
          {faculty.filter((f) => !f.is_founder).length > 0 && (
            <>
              <div className="flex items-center gap-3 pt-2">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#E8E1D5] to-transparent" />
                <span className="text-[10px] font-black text-[#003399] uppercase tracking-widest">
                  + Full-time Subject Leads
                </span>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#E8E1D5] to-transparent" />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
                {faculty.filter((f) => !f.is_founder).map((fac, idx) => {
                  const initials = (fac.name || "").split(" ").map((s) => s[0]).filter((c) => /[A-Z]/i.test(c)).slice(0, 2).join("").toUpperCase() || "TR";
                  const bg = fac.image_url
                    ? undefined
                    : { backgroundImage: `linear-gradient(135deg, ${fac.monogram_from || "#003399"}, ${fac.monogram_to || "#1E3A8A"})` };
                  return (
                    <div
                      key={idx}
                      data-testid={`faculty-tile-${idx}`}
                      className="p-4 rounded-2xl bg-white border-2 border-orange-300 hover:border-orange-500 transition-all shadow-sm hover:shadow-md group"
                    >
                      <div className="flex items-center gap-3">
                        {fac.image_url ? (
                          <img src={optimg(fac.image_url, 128)} alt={fac.name} width="56" height="56" loading="lazy" decoding="async" className="w-14 h-14 rounded-xl object-cover shrink-0 border border-gray-200" />
                        ) : (
                          <div
                            style={bg}
                            className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-black text-lg shrink-0 shadow-inner"
                          >
                            {initials}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <h4 className="text-sm font-black text-[#071E54] truncate blur-sm select-none pointer-events-none">{fac.name}</h4>
                          <p className="text-[10px] font-bold text-[#003399] truncate">{fac.role}</p>
                          <p className="text-[10px] text-gray-500 font-medium truncate">{fac.pedigree}</p>
                        </div>
                      </div>
                      {fac.pillar && (
                        <span className="mt-3 inline-block text-[9px] font-black text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 uppercase tracking-widest">
                          {fac.pillar}
                        </span>
                      )}
                      <p className="text-[11px] text-gray-600 leading-relaxed font-medium mt-2 line-clamp-2">
                        {fac.bio}
                      </p>
                      <div className="mt-2 pt-2 border-t border-gray-100 text-[10px] text-emerald-700 font-bold">
                        {fac.achievements}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

        </div>
      </section>

      <SectionDivider label="Scholarship Exam" />

      {/* 3b. MINDSET OF TOPPERS — national AIR-1 quotes */}
      <section data-section="mindset" data-section-label="Topper Mindset" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <TopperMindset />
      </section>

      {/* 6. RTSE 3.0 SCHOLARSHIP BANNER */}
      </ScrollFade>
      <ScrollFade>
      <section data-section="rtse" data-section-label="RTSE 3.0" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl bg-gradient-to-r from-[#003399] via-[#0D47A1] to-[#071E54] text-white p-8 sm:p-12 shadow-xl flex flex-col lg:flex-row items-center justify-between gap-8">
          
          <div className="space-y-3.5 max-w-2xl">
            <span className="px-3 py-1 rounded-full bg-amber-400 text-[#071E54] text-xs font-black uppercase tracking-wider">
              RTSE 3.0 SCHOLARSHIP EXAMINATION
            </span>
            <h2 className="text-2xl sm:text-4xl font-black text-white leading-tight">
              Up to 90% Scholarship & Scratch Cards up to ₹10,000
            </h2>
            <p className="text-xs sm:text-sm text-slate-200 leading-relaxed font-medium">
              Direct entry into the Genius 40 Super Batch. Register online in 2 minutes to generate and download your official PDF Admit Card with QR verification.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto shrink-0">
            <Link
              to="/rtse"
              data-testid="banner-rtse-register-cta"
              className="px-6 py-3.5 rounded-xl bg-amber-400 hover:bg-amber-300 text-[#071E54] font-black text-sm shadow-lg text-center flex items-center justify-center space-x-2 transition-all"
            >
              <span>Instant PDF Admit Card</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              onClick={() => navigate("/demo")}
              className="px-6 py-3.5 rounded-xl bg-[#FDFAF3] hover:bg-gray-100 text-[#003399] font-bold text-sm shadow-md"
            >
              Free 1-Day Trial Pass
            </button>
          </div>

        </div>
      </section>
      </ScrollFade>

      {/* Demo Booking Modal */}
      {isDemoModalOpen && (
        <Suspense fallback={null}>
          <DemoBookingModal isOpen={isDemoModalOpen} onClose={() => setIsDemoModalOpen(false)} />
        </Suspense>
      )}

    </div>
  );
}
